import {Card, Container, Group, Menu, rem, Space, Tabs, TabsValue, Text, Title, UnstyledButton} from "@mantine/core";
import {PropsWithChildren, useCallback, useMemo} from "react";
import {ShiftEntity} from "@ots-frontend/ots-data";
import {ShiftDetails} from "./ShiftDetails";
import {createContentStyles} from "../../../theme/useContentStyles";
import {IconChevronDown, IconMap} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {ShiftAction, ShiftActionMenuDropdown} from "../../menu/ShiftActionMenu/ShiftActionMenu";
import {useLocation, useNavigate} from "react-router-dom";
import {TabItem} from "../../tabs/Tabs/Tabs";
import {HasAccess} from "../../auth/HasAccess/HasAccess";

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type ShiftProfileProps = PropsWithChildren<{
  shift: ShiftEntity
}>

export const ShiftProfile = (props: ShiftProfileProps) => {
  const {shift} = props;

  const {classes} = useStyles();

  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();
  const {t} = useTranslation()

  const onActionClick = useCallback((action: ShiftAction) => {
    if (action === "EDIT") {
      navigate(`/shifts/form`, {state: {id: shift.id}})
    }
  }, [shift, navigate])

  const onTabChange = useCallback((value: TabsValue) => {
    if (value !== 'map') {
      navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})
    } else if (value === 'map') {
      navigate(`map?id=${shift.id}&database=${shift.database}`)
    }
  }, [navigate, pathname, search, state, shift])

  const tabs = useMemo(() => {
    const tabs: TabItem[] = [
      {
        key: "details",
        value: "Details"
      },
      {
        key: "map",
        value: "Map",
        icon: <IconMap size={14}/>
      }
    ]
    return tabs
  }, [])

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key} icon={tab.icon}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>
            {t("common.entity.shift.profile.title", "Shift Details")}
          </Title>

          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <ShiftActionMenuDropdown
                shift={shift}
                onActionClick={onActionClick}/>
            </Menu.Dropdown>
          </Menu>

        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={onTabChange}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <ShiftDetails shift={shift}/>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
